import { useCallback, useEffect, useRef, useState } from "react";

import { AddressFinder } from "@ideal-postcodes/address-finder";
import "@ideal-postcodes/address-finder/css/address-finder.min.css";

import { getIdealPostcodesApiKey } from "@/core/config";
import {
  applyAddressToForm,
  getAddressFinderRestrictCountries,
  getDefaultCountryIso3
} from "@/utils/idealPostcodesAddress";

function alignDropdownToPill(controller, pillElement) {
  const pill = pillElement || controller.input.closest("[data-address-finder-pill]");
  const dropdown = controller.mainComponent;

  if (!pill || !dropdown) {
    return;
  }

  controller.container.style.position = "static";
  dropdown.style.position = "absolute";
  dropdown.style.left = "0";
  dropdown.style.top = `${pill.offsetHeight}px`;
  dropdown.style.width = `${pill.offsetWidth}px`;
  dropdown.style.marginTop = "0";
  dropdown.style.minWidth = "unset";
  dropdown.style.borderTop = "none";
  dropdown.style.borderRadius = "0 0 16px 16px";
}

export default function useAddressFinder({
  disabled = false,
  shippingCountries = [],
  countryIso2,
  countryValueType = "isoCode",
  updateCountry = true,
  fieldNames,
  setValue,
  onAddressPopulated,
  placeholder = "Postcode lookup",
  pillRef
}) {
  const inputRef = useRef(null);
  const controllerRef = useRef(null);
  const [available, setAvailable] = useState(true);
  const apiKey = getIdealPostcodesApiKey();

  const onAddressRetrievedRef = useRef(null);
  const shippingCountriesRef = useRef(shippingCountries);
  shippingCountriesRef.current = shippingCountries;

  const onAddressPopulatedRef = useRef(onAddressPopulated);
  onAddressPopulatedRef.current = onAddressPopulated;

  onAddressRetrievedRef.current = address => {
    applyAddressToForm({
      address,
      fieldNames,
      setValue,
      shippingCountries: shippingCountriesRef.current,
      countryValueType,
      updateCountry,
      fallbackIso2: countryIso2
    });
    onAddressPopulatedRef.current?.(address);
  };

  const handleAddressRetrieved = useCallback(function handleAddressRetrieved(address) {
    onAddressRetrievedRef.current?.(address);
  }, []);

  const syncDropdownPosition = useCallback(function syncDropdownPosition(controller) {
    alignDropdownToPill(controller, pillRef?.current);
  }, [pillRef]);

  useEffect(() => {
    if (!apiKey || disabled || !available) {
      return undefined;
    }

    if (!updateCountry && !countryIso2) {
      return undefined;
    }

    const input = inputRef.current;
    if (!input) {
      return undefined;
    }

    const lockCountry = !updateCountry;
    const restrictCountries = getAddressFinderRestrictCountries({
      shippingCountries: shippingCountriesRef.current,
      countryIso2,
      lockCountry
    });
    const controller = AddressFinder.setup({
      apiKey,
      inputField: input,
      defaultCountry: getDefaultCountryIso3(countryIso2),
      restrictCountries,
      detectCountry: false,
      hideToolbar: lockCountry,
      checkKey: true,
      injectStyle: true,
      alignToInput: false,
      offset: 0,
      msgPlaceholder: placeholder,
      containerStyle: { width: "100%", position: "static" },
      mainStyle: { marginTop: 0 },
      onFailedCheck: () => {
        setAvailable(false);
      },
      onLoaded() {
        if (!this.input.value) {
          this.input.placeholder = placeholder;
        }
        syncDropdownPosition(this);
      },
      onOpen() {
        syncDropdownPosition(this);
      },
      onAddressRetrieved: handleAddressRetrieved
    });

    controllerRef.current = controller;
    syncDropdownPosition(controller);

    return () => {
      controllerRef.current = null;
      try {
        controller.detach();
      } catch {
        // Ignore teardown errors from third-party widget cleanup.
      }
    };
  }, [
    apiKey,
    available,
    countryIso2,
    disabled,
    handleAddressRetrieved,
    placeholder,
    syncDropdownPosition,
    updateCountry
  ]);

  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller || !countryIso2) {
      return;
    }

    const iso3 = getDefaultCountryIso3(countryIso2);
    const details = controller.options.contexts[iso3];
    if (details) {
      controller.applyContext(details, false);
    }
  }, [countryIso2]);

  return {
    inputRef,
    available: Boolean(apiKey && available)
  };
}
